import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Cleave from 'cleave.js/react';
import classNames from 'classnames';

import { AsteriskIcon } from '../../icons';
import ErrorText from '../ErrorText';

import { getCleaveOptions, getPlaceholder } from './utilities/helpers';

import styles from './_index.module.scss';

const Input = ({
  autoComplete,
  autoFocus,
  className,
  'data-testid': dataTestId,
  error,
  id,
  isDisabled,
  isRequired,
  label,
  max,
  maxLength,
  min,
  onBlur,
  onChange,
  onPaste,
  placeholder,
  type,
  value,
}) => {
  const formats = ['decimal', 'dollar', 'percentage'];
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, []);

  const handleBlur = (event) => {
    if (onBlur) {
      onBlur(event?.target?.rawValue || event?.target?.value);
    }
  };
  const handleChange = (event) => {
    onChange(event?.target?.rawValue || event?.target?.value);
  };
  const handlePaste = (event) => {
    if (onPaste) {
      const value = (event.clipboardData || window.clipboardData).getData('text');
      onPaste(event, value);
    }
  };

  const otherClassNames = classNames(
    styles['input'],
    {
      [styles['is-percentage']]: type === 'percentage',
      [styles['has-error']]: error,
    },
    className
  );

  const cleaveOptions = getCleaveOptions(type);
  const props = {
    className: otherClassNames,
    disabled: isDisabled,
    id,
    onBlur: handleBlur,
    onChange: handleChange,
    onPaste: handlePaste,
    placeholder: placeholder || getPlaceholder(type),
    value,
  };

  return (
    <div>
      {label && (
        <label className={styles['input-label']} htmlFor={id}>
          {label}
          {isRequired && <AsteriskIcon />}
        </label>
      )}
      {formats.includes(type) ? (
        <Cleave {...props} options={cleaveOptions} />
      ) : (
        <input
          {...props}
          autoComplete={autoComplete}
          data-testid={dataTestId}
          max={max}
          maxLength={maxLength}
          min={min}
          ref={inputRef}
          type={type}
        />
      )}
      {error && <ErrorText className={styles['input-error']}>{error}</ErrorText>}
    </div>
  );
};

Input.defaultProps = {
  autoFocus: false,
  isDisabled: false,
  isRequired: false,
  onChange: () => {},
  type: 'text',
};

Input.propTypes = {
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  'data-testid': PropTypes.string,
  error: PropTypes.string,
  id: PropTypes.string,
  isDisabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  max: PropTypes.number,
  maxLength: PropTypes.number,
  min: PropTypes.number,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onPaste: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Input;
